import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";

// IMPORT RESULTS DIALOG
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    height: "auto",
    width: "900px",
    maxWidth: "none",
  },
}));

type InvalidRow = {
  index: number;
  reason: string;
  row: Record<string, unknown>;
};

export type InvalidRecordsColumn = {
  field: string;
  label: React.ReactNode;
  renderLogic: (row: InvalidRow) => React.ReactNode;
};

const defaultInvalidColumns: InvalidRecordsColumn[] = [
  {
    field: "index",
    label: <div>Row No.</div>,
    renderLogic: (row) => (
      <p className="text-neutral-90 font-normal">{row.index + 1}</p>
    ),
  },
  {
    field: "itemName",
    label: <div>Item Name</div>,
    renderLogic: (row) => (
      <p className="text-neutral-90 font-normal">
        {row.row.itemName ? String(row.row.itemName) : "N/A"}
      </p>
    ),
  },
  {
    field: "itemCode",
    label: <div>Item Code</div>,
    renderLogic: (row) => (
      <p className="text-neutral-90 font-normal">
        {row.row.itemCode ? String(row.row.itemCode) : "N/A"}
      </p>
    ),
  },
  {
    field: "type",
    label: <div>Type</div>,
    renderLogic: (row) => (
      <p className="text-neutral-90 font-normal">
        {row.row.type ? String(row.row.type) : "N/A"}
      </p>
    ),
  },
  {
    field: "reason",
    label: <div>Error Reason</div>,
    renderLogic: (row) => (
      <Tooltip title={row.reason} arrow placement="top">
        <Chip
          label={row.reason}
          size="small"
          style={{
            backgroundColor: "#ffebee",
            color: "#d32f2f",
            fontSize: "12px",
            maxWidth: "300px",
            height: "auto",
            whiteSpace: "normal",
            wordBreak: "break-word",
            cursor: "default",
          }}
        />
      </Tooltip>
    ),
  },
];

interface ImportResultsDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  results: {
    summary: {
      totalRecords: number;
      validRecords: number;
      invalidRecords: number;
      createdInventory: number;
      totalProcessed: number;
    };
    valid: Array<Record<string, unknown>>;
    invalid: Array<InvalidRow>;
    message: string;
  } | null;
  invalidColumns?: InvalidRecordsColumn[];
}

export default function ImportResultsDialog({
  isOpen = false,
  handleClose,
  results,
  invalidColumns,
}: Readonly<ImportResultsDialogProps>) {
  const columns = invalidColumns ?? defaultInvalidColumns;
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Reset pagination when dialog opens or results change
  React.useEffect(() => {
    if (isOpen && results) {
      setCurrentPage(1);
      setRowsPerPage(10);
    }
  }, [isOpen, results]);

  if (!results) {
    return null;
  }

  const { summary, invalid } = results;

  // Calculate pagination
  const totalPages = Math.ceil(invalid.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedInvalid = invalid.slice(startIndex, endIndex);

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Records",
      value: summary.totalRecords.toString(),
      color: "#1976d2",
      bgColor: "#e3f2fd",
    },
    {
      title: "Valid Records",
      value: summary.validRecords.toString(),
      color: "#2e7d32",
      bgColor: "#e8f5e8",
    },
    {
      title: "Invalid Records",
      value: summary.invalidRecords.toString(),
      color: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="import-results-dialog-title"
      open={isOpen}
    >
      <div
        className="flex justify-between items-center rounded-lg"
        style={{ margin: "14px 24px" }}
      >
        <Heading
          title={
            results.summary.invalidRecords > 0
              ? "Import Completed with Issues"
              : "Import Completed Successfully"
          }
        />
        <button
          onClick={handleClose}
          className="cursor-pointer border-none bg-transparent p-0"
          type="button"
          aria-label="Close dialog"
        >
          <CloseIcon />
        </button>
      </div>

      <DialogContent
        dividers
        style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
      >
        <div className="flex flex-col gap-6">
          {/* Summary Section */}
          <div className="flex gap-4 justify-center">
            {summaryCards.map((card) => (
              <Box
                key={card.title}
                sx={{
                  backgroundColor: card.bgColor,
                  color: card.color,
                  padding: "12px 20px",
                  borderRadius: "8px",
                  textAlign: "center",
                  minWidth: "120px",
                }}
              >
                <Typography
                  variant="h6"
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    marginBottom: "4px",
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            ))}
          </div>

          {/* Message */}
          <div className="text-center">
            <Typography
              variant="body1"
              style={{
                color: summary.invalidRecords > 0 ? "#d32f2f" : "#2e7d32",
                fontWeight: "500",
              }}
            >
              {results.message}
            </Typography>
          </div>

          {/* Invalid Records Table */}
          {invalid.length > 0 && (
            <div>
              <Typography
                variant="h6"
                style={{
                  marginBottom: "16px",
                  fontWeight: "600",
                  color: "#d32f2f",
                }}
              >
                Invalid Records Details
              </Typography>
              <Table
                columns={columns}
                rows={paginatedInvalid}
                paginationProps={{
                  isPagination: true,
                  page: currentPage,
                  totalPages: totalPages,
                  totalCount: invalid.length,
                  limit: rowsPerPage.toString(),
                  rowsPerPageOptions: [10, 25, 50],
                  onPageChange: (page: number) => {
                    setCurrentPage(page);
                  },
                  handleChangeRowsPerPage: (newRowsPerPage: string) => {
                    setRowsPerPage(parseInt(newRowsPerPage));
                    setCurrentPage(1); // Reset to first page when changing rows per page
                  },
                }}
              />
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end mt-4">
            <Button
              label="Close"
              variant="contained"
              onClickCb={handleClose}
              style={{
                width: "120px",
                height: "44px",
                padding: "12px 20px",
                fontSize: "16px",
                backgroundColor: "#005596",
                color: "#FFFFFF",
                border: "1px solid #005596",
                fontFamily: "Inter",
              }}
            />
          </div>
        </div>
      </DialogContent>
    </BootstrapDialog>
  );
}
